import React, { useState, useRef, useEffect } from 'react';
import './ChatBot.css';

interface Message {
  id: number;
  text: string;
  isBot: boolean;
  timestamp: Date;
}

const ChatBot: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 1,
      text: "Hello! I'm PolicyBot, your insurance assistant. I can help you with questions about auto, home, life, and health insurance. What would you like to know?",
      isBot: true,
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const getInsuranceResponse = (userMessage: string): string => {
    const message = userMessage.toLowerCase();

    // Auto Insurance
    if (message.includes('auto') || message.includes('car') || message.includes('vehicle')) {
      if (message.includes('cost') || message.includes('price') || message.includes('expensive')) {
        return "Auto insurance costs depend on factors like your driving record, age, location, vehicle type, and coverage level. On average, drivers pay $1,200-$1,800 annually. To lower costs, consider bundling policies, maintaining good credit, or increasing your deductible.";
      }
      if (message.includes('coverage') || message.includes('covers')) {
        return "Auto insurance typically includes liability (required by law), collision, comprehensive, personal injury protection, and uninsured motorist coverage. Liability covers damage you cause to others, while collision and comprehensive protect your vehicle.";
      }
      if (message.includes('deductible')) {
        return "An auto insurance deductible is the amount you pay out-of-pocket before your insurance kicks in. Common deductibles range from $250-$1,000. Higher deductibles mean lower premiums but more cost when filing a claim.";
      }
      return "Auto insurance protects you financially when driving. It's required by law in most states and covers liability, collision, and comprehensive damages. Would you like to know about specific coverage types or costs?";
    }

    // Home Insurance
    if (message.includes('home') || message.includes('house') || message.includes('property')) {
      if (message.includes('cost') || message.includes('price')) {
        return "Home insurance typically costs $1,200-$2,000 annually for average coverage. Factors affecting cost include home value, location, age of home, and coverage amount. Coastal areas and regions prone to natural disasters usually cost more.";
      }
      if (message.includes('coverage') || message.includes('covers')) {
        return "Home insurance covers your dwelling, personal belongings, liability protection, and additional living expenses. It protects against fire, theft, vandalism, and many natural disasters. Floods and earthquakes typically require separate policies.";
      }
      if (message.includes('claim')) {
        return "To file a home insurance claim: 1) Contact your insurer immediately, 2) Document damage with photos, 3) Make temporary repairs to prevent further damage, 4) Keep receipts, 5) Meet with the adjuster. Most claims are processed within 30 days.";
      }
      return "Home insurance protects your property and belongings from covered perils like fire, theft, and storms. It also provides liability coverage if someone is injured on your property. Would you like details about coverage types?";
    }

    // Life Insurance
    if (message.includes('life') && message.includes('insurance')) {
      if (message.includes('type') || message.includes('kinds')) {
        return "There are two main types: Term life (temporary coverage for specific period, cheaper) and Whole life (permanent coverage with cash value, more expensive). Term is good for temporary needs, whole life for estate planning.";
      }
      if (message.includes('cost') || message.includes('price')) {
        return "Life insurance costs depend on age, health, lifestyle, and coverage amount. A healthy 30-year-old might pay $20-40/month for $500,000 term coverage. Whole life insurance costs 10-20 times more than term for the same coverage.";
      }
      if (message.includes('need') || message.includes('much')) {
        return "A common rule is 10-12 times your annual income. Consider your debts, mortgage, children's education costs, and spouse's financial needs. The goal is replacing your income so your family maintains their lifestyle.";
      }
      return "Life insurance provides financial protection for your beneficiaries when you pass away. It helps cover final expenses, debts, and replaces lost income. Would you like to know about term vs. whole life insurance?";
    }

    // Health Insurance
    if (message.includes('health') && message.includes('insurance')) {
      if (message.includes('deductible')) {
        return "A health insurance deductible is what you pay before insurance covers costs. High-deductible plans have lower premiums but you pay more upfront. Low-deductible plans have higher premiums but less out-of-pocket costs initially.";
      }
      if (message.includes('network') || message.includes('provider')) {
        return "Insurance networks are groups of doctors and hospitals that accept your plan. In-network providers cost less because they have agreements with your insurer. Out-of-network providers typically cost significantly more.";
      }
      if (message.includes('copay') || message.includes('coinsurance')) {
        return "A copay is a fixed amount you pay for services (like $25 for a doctor visit). Coinsurance is a percentage you pay after meeting your deductible (like 20% of a hospital bill). Both are your share of healthcare costs.";
      }
      return "Health insurance helps cover medical expenses including doctor visits, hospital stays, prescriptions, and preventive care. Plans vary by deductible, network, and coverage levels. Would you like to know about specific aspects?";
    }

    // General Insurance Questions
    if (message.includes('deductible')) {
      return "A deductible is the amount you pay out-of-pocket before your insurance coverage begins. Higher deductibles usually mean lower premiums. Choose a deductible you can afford to pay if you need to file a claim.";
    }

    if (message.includes('premium')) {
      return "An insurance premium is the amount you pay (monthly, quarterly, or annually) to keep your policy active. Factors affecting premiums include coverage amount, deductible, risk factors, and your claims history.";
    }

    if (message.includes('claim')) {
      return "To file an insurance claim: 1) Contact your insurance company immediately, 2) Provide detailed information about the incident, 3) Document everything with photos/receipts, 4) Follow up regularly, 5) Keep records of all communications.";
    }

    if (message.includes('bundling') || message.includes('bundle')) {
      return "Insurance bundling means buying multiple policies from the same company (like auto + home). Benefits include discounts (typically 5-25% savings), simplified billing, and having one point of contact for all policies.";
    }

    if (message.includes('coverage')) {
      return "Insurance coverage is the protection your policy provides against specific risks and losses. Different types include liability (covers others), collision (covers your property), comprehensive (covers various perils), and more.";
    }

    // Greetings and common phrases
    if (message.includes('hello') || message.includes('hi') || message.includes('hey')) {
      return "Hello! I'm here to help with your insurance questions. You can ask me about auto, home, life, or health insurance - coverage types, costs, claims, or anything else insurance-related!";
    }

    if (message.includes('thank')) {
      return "You're welcome! I'm happy to help with any other insurance questions you might have. Feel free to ask about different types of coverage, costs, or claims processes.";
    }

    if (message.includes('help')) {
      return "I can help you with questions about:\n• Auto Insurance (coverage, costs, claims)\n• Home Insurance (protection, pricing, policies)\n• Life Insurance (types, amounts, benefits)\n• Health Insurance (plans, networks, costs)\n• General insurance concepts (deductibles, premiums, claims)\n\nWhat specific topic interests you?";
    }

    // Default response
    return "I'd be happy to help with that insurance question! Could you be more specific? I can provide information about auto, home, life, or health insurance - including coverage types, costs, claims processes, and general insurance concepts. What would you like to know?";
  };

  const handleSendMessage = () => {
    if (inputValue.trim() === '') return;

    const userMessage: Message = {
      id: messages.length + 1,
      text: inputValue,
      isBot: false,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);

    // Simulate bot typing delay
    setTimeout(() => {
      const botResponse: Message = {
        id: messages.length + 2,
        text: getInsuranceResponse(inputValue),
        isBot: true,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, botResponse]);
      setIsTyping(false);
    }, 1000 + Math.random() * 1000); // Random delay between 1-2 seconds
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSendMessage();
    }
  };

  const quickQuestions = [
    "What does auto insurance cover?",
    "How much life insurance do I need?",
    "What's the difference between deductible and premium?",
    "How do I file a claim?",
    "What is insurance bundling?"
  ];

  const handleQuickQuestion = (question: string) => {
    setInputValue(question);
  };

  const handleGoHome = () => {
    // This will be handled by the back button in the main app
    console.log('Go home clicked - handled by parent app');
  };

  return (
    <div className="chatbot-container">
      <div className="chatbot-header">
        <div className="header-left">
          <div className="bot-avatar">🤖</div>
          <div className="bot-info">
            <h3>PolicyBot</h3>
            <span className="status">Online • Insurance Assistant</span>
          </div>
        </div>
        <button className="home-button" onClick={handleGoHome} title="Go to Home">
          🏠 Home
        </button>
      </div>

      <div className="messages-container">
        {messages.map((message) => (
          <div key={message.id} className={`message ${message.isBot ? 'bot-message' : 'user-message'}`}>
            <div className="message-content">
              <div className="message-text">{message.text}</div>
              <div className="message-time">
                {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
          </div>
        ))}
        
        {isTyping && (
          <div className="message bot-message">
            <div className="message-content">
              <div className="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {messages.length === 1 && (
        <div className="quick-questions">
          <h4>Quick Questions:</h4>
          <div className="quick-buttons">
            {quickQuestions.map((question, index) => (
              <button
                key={index}
                className="quick-button"
                onClick={() => handleQuickQuestion(question)}
              >
                {question}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="input-container">
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Ask me about insurance..."
          className="message-input"
        />
        <button onClick={handleSendMessage} className="send-button">
          Send
        </button>
      </div>
    </div>
  );
};

export default ChatBot;